/**
 * Socket 配置模块
 * 包含 WebSocket 连接的基础配置、事件类型定义和状态枚举
 */

import { getWsUrl } from '@/utils/env'

// 默认 WebSocket 服务器地址，可通过环境变量覆盖，支持动态域名
export const DEFAULT_URL = getWsUrl()
export const LOCAL_STORAGE_TOKEN_KEY = 'access_token'
 
export const IS_LOG = true
// Socket.io 客户端配置选项
export const DEFAULT_OPTIONS = {
  // 重连配置
  reconnection: true,           // 启用自动重连
  reconnectionAttempts: 5,      // 最大重连次数
  reconnectionDelay: 1000,      // 重连延迟（毫秒）
  reconnectionDelayMax: 5000,   // 最大重连延迟（毫秒）
  
  // 超时配置
  timeout: 20000,               // 连接超时时间（毫秒）
  
  // 传输配置
  transports: ['websocket', 'polling'],  // 优先使用 WebSocket，降级到轮询
  
  // 心跳配置
  pingInterval: 25000,          // 心跳间隔（毫秒）
  pingTimeout: 5000,            // 心跳超时（毫秒）
}

/**
 * Socket 事件类型枚举
 * 定义了所有可能的 Socket 事件类型
 */
export enum SocketEvent {
  // 连接事件
  CONNECT = 'connect',           // 连接成功
  DISCONNECT = 'disconnect',     // 连接断开
  ERROR = 'error',              // 错误事件
  RECONNECT_ATTEMPT = 'reconnectAttempt',  // 重连尝试
  RECONNECT_FAILED = 'reconnectFailed',    // 重连失败
  
  // 认证事件
  AUTH = 'auth',                // 认证请求
  AUTH_SUCCESS = 'authSuccess', // 认证成功
  AUTH_FAILED = 'authFailed',   // 认证失败
  
  // 房间事件
  JOIN_ROOM = 'joinRoom',      // 加入房间
  USER_JOIN_ROOM = 'userJoinRoom', // 用户加入房间
  ROOM_USERS = 'roomUsers', // 用户离开房间
  LEAVE_ROOM = 'leaveRoom',    // 离开房间
  ROOM_JOINED = 'roomJoined',  // 已加入房间
  ROOM_LEFT = 'roomLeft',      // 已离开房间
  
  // 消息事件
  MESSAGE = 'message',          // 普通消息
  PRIVATE_MESSAGE = 'privateMessage',  // 私聊消息
  BROADCAST = 'broadcast',      // 广播消息
  
  // 系统事件
  PING = 'ping',               // 心跳请求
  PONG = 'pong',               // 心跳响应
}

/**
 * 事件优先级枚举
 * 用于控制事件处理器的执行顺序
 */
export enum EventPriority {
  HIGH = 3,     // 高优先级，最先执行
  NORMAL = 2,   // 普通优先级
  LOW = 1,      // 低优先级，最后执行
}

/**
 * 认证信息接口
 * 用于用户认证的数据结构
 */
export interface AuthInfo {
  token: string                // 认证令牌
  userId?: string             // 用户ID（可选）
  deviceId?: string           // 设备ID（可选）
}

/**
 * 房间信息接口
 * 用于房间操作的数据结构
 */
export interface RoomInfo {
  roomId: string              // 房间ID
  name?: string              // 房间名称（可选）
  type?: string              // 房间类型（可选）
  members?: number           // 成员数量（可选）
}

/**
 * 服务器响应接口
 * 用于统一处理服务器返回的数据结构
 */
export interface ServerResponse<T = any> {
  code: number               // 响应状态码
  message?: string          // 响应消息（可选）
  data?: T                  // 响应数据（可选）
  success: boolean          // 是否成功
}

/**
 * Socket 状态枚举
 * 用于表示 Socket 连接的不同状态
 */
export enum SocketStatus {
  CONNECTING = 'connecting',     // 正在连接
  CONNECTED = 'connected',       // 已连接
  DISCONNECTED = 'disconnected', // 已断开
  RECONNECTING = 'reconnecting', // 正在重连
  ERROR = 'error',              // 发生错误
  AUTHENTICATED = 'authenticated', // 已认证
  AUTH_FAILED = 'auth_failed'    // 认证失败
}

// 重连策略
export const RECONNECTION_STRATEGY = {
  maxAttempts: 5,
  initialDelay: 1000,
  maxDelay: 5000,
  backoffFactor: 1.5
}

// 心跳配置
export const HEARTBEAT_CONFIG = {
  interval: 30000,
  timeout: 5000,
  maxMissed: 3
}

// 错误码枚举
export enum SocketErrorCode {
  CONNECTION_FAILED = 1001,
  AUTH_FAILED = 1002,
  ROOM_JOIN_FAILED = 1003,
  MESSAGE_SEND_FAILED = 1004,
  HEARTBEAT_TIMEOUT = 1005,
  RECONNECT_FAILED = 1006
}
